import { Inspector } from './Inspector';
import { MachineTimelineEntry } from './types';

export class InspectorConsole<
  TEvent extends { type: string },
> extends Inspector<TEvent> {
  private logPrefix: string;

  constructor(args: { name?: string; maxTimelineSize?: number } = {}) {
    super({ ...args, debug: true });
    this.logPrefix = `[FSM:${this.name}]`;
  }

  add(entry: MachineTimelineEntry<TEvent>): boolean {
    const wasAdded = super.add(entry);

    if (wasAdded) {
      this.logEntry(entry);
    }

    return wasAdded;
  }

  private logEntry(entry: MachineTimelineEntry<TEvent>): void {
    const timestamp = new Date().toISOString();

    switch (entry.type) {
      case 'fsm.transition.take':
        console.log(
          `${this.logPrefix} TRANSITION`,
          `${entry.data?.previousState} → ${entry.data?.nextState}`,
          entry.event ? `(event: ${entry.event.type})` : '',
          { timestamp, entry }
        );
        break;

      case 'fsm.action.resolve':
        console.log(
          `${this.logPrefix} ACTION`,
          entry.data?.action || 'unknown',
          entry.event ? `(event: ${entry.event.type})` : '',
          { timestamp, entry }
        );
        break;

      case 'fsm.action.error':
        console.error(
          `${this.logPrefix} ACTION ERROR`,
          entry.data?.action || 'unknown',
          entry.event ? `(event: ${entry.event.type})` : '',
          { timestamp, entry, error: (entry as any).error }
        );
        break;

      case 'fsm.service.resolve':
        console.log(
          `${this.logPrefix} SERVICE RESOLVED`,
          entry.data?.state || 'unknown',
          entry.event ? `(event: ${entry.event.type})` : '',
          { timestamp, entry }
        );
        break;

      case 'fsm.service.error':
        console.error(
          `${this.logPrefix} SERVICE ERROR`,
          entry.data?.state || 'unknown',
          entry.event ? `(event: ${entry.event.type})` : '',
          { timestamp, entry, error: (entry as any).error }
        );
        break;

      case 'fsm.guard.take':
        console.log(
          `${this.logPrefix} GUARD PASSED`,
          entry.description || 'unknown guard',
          entry.event ? `(event: ${entry.event.type})` : '',
          { timestamp, entry }
        );
        break;

      case 'fsm.guard.skip':
        console.log(
          `${this.logPrefix} GUARD FAILED`,
          entry.description || 'unknown guard',
          entry.event ? `(event: ${entry.event.type})` : '',
          { timestamp, entry }
        );
        break;

      case 'fsm.event.ignore':
        console.warn(
          `${this.logPrefix} EVENT IGNORED`,
          entry.event ? entry.event.type : 'unknown event',
          'No transition defined for current state',
          { timestamp, entry }
        );
        break;

      default:
        console.log(
          `${this.logPrefix} ${entry.type.toUpperCase()}`,
          entry.description || '',
          entry.event ? `(event: ${entry.event.type})` : '',
          { timestamp, entry }
        );
    }
  }

  start(): void {
    super.start();
    console.log(`${this.logPrefix} Console logging started`);
  }

  stop(): void {
    super.stop();
    console.log(`${this.logPrefix} Console logging stopped`);
  }
}
